import { db } from './firebase';
import { doc, setDoc, deleteDoc, collection, getDocs, query, limit, Timestamp } from 'firebase/firestore';

/**
 * Initialize sample vouchers and coupons in the database
 * This should only be run once to set up initial data
 */
export async function initializeDiscounts() {
  try {
    console.log('Initializing sample discounts...');
    
    // First delete any existing sample discounts
    await deleteSampleDiscounts();

    // Create WELCOME50 voucher
    await setDoc(doc(db, 'vouchers', 'WELCOME50'), {
      code: 'WELCOME50',
      type: 'percentage',
      value: 10,
      description: 'Welcome voucher - 10% off your first order',
      minOrderAmount: 200,
      maxDiscount: 100,
      usageLimit: 1,
      usedCount: 0,
      enabled: true,
      validFrom: Timestamp.fromDate(new Date('2024-01-01T00:00:00Z')),
      validUntil: Timestamp.fromDate(new Date('2027-12-31T23:59:59Z')),
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      restrictions: {
        userGroups: ['new_user'],
        excludedProducts: [],
        excludedCategories: [],
        paymentMethods: ['all']
      },
      usageHistory: []
    });

    // Create RUTHDAY voucher
    await setDoc(doc(db, 'vouchers', 'RUTHDAY'), {
      code: 'RUTHDAY',
      type: 'fixed',
      value: 50,
      description: 'Ruths Day',
      minOrderAmount: 500,
      maxDiscount: 50,
      usageLimit: 2,
      usedCount: 0,
      enabled: true,
      validFrom: Timestamp.fromDate(new Date('2025-08-21T00:00:00Z')),
      validUntil: Timestamp.fromDate(new Date('2025-08-25T23:59:59Z')),
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      restrictions: {
        userGroups: ['all'],
        excludedProducts: [],
        excludedCategories: [],
        paymentMethods: ['all']
      },
      usageHistory: []
    });

    // Create SAVE20 coupon
    await setDoc(doc(db, 'coupons', 'SAVE20'), {
      code: 'SAVE20',
      type: 'fixed',
      value: 20,
      description: 'Save ₱20 on orders above ₱100',
      minOrderAmount: 100,
      maxDiscount: 20,
      usageLimit: 1,
      usedCount: 0,
      enabled: true,
      validFrom: Timestamp.fromDate(new Date('2024-01-01T00:00:00Z')),
      validUntil: Timestamp.fromDate(new Date('2027-12-31T23:59:59Z')),
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      category: 'seasonal',
      restrictions: {
        userGroups: ['all'],
        excludedProducts: [],
        excludedCategories: [],
        paymentMethods: ['all']
      },
      usageHistory: []
    });

    console.log('✅ Sample discounts initialized successfully!');
    return { success: true };

  } catch (error) {
    console.error('❌ Error initializing discounts:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete existing sample discounts (to recreate with correct format)
 */
export async function deleteSampleDiscounts() {
  try {
    console.log('Deleting existing sample discounts...');
    
    // Delete WELCOME50 voucher if it exists
    try {
      await deleteDoc(doc(db, 'vouchers', 'WELCOME50'));
      console.log('Deleted WELCOME50 voucher');
    } catch (e) {
      console.log('WELCOME50 voucher not found or already deleted');
    }
    
    // Delete RUTHDAY voucher if it exists (it has wrong date format)
    try {
      await deleteDoc(doc(db, 'vouchers', 'RUTHDAY'));
      console.log('Deleted RUTHDAY voucher');
    } catch (e) {
      console.log('RUTHDAY voucher not found or already deleted');
    }
    
    // Delete SAVE20 coupon if it exists
    try {
      await deleteDoc(doc(db, 'coupons', 'SAVE20'));
      console.log('Deleted SAVE20 coupon');
    } catch (e) {
      console.log('SAVE20 coupon not found or already deleted');
    }
    
    console.log('✅ Sample discounts deleted successfully!');
    return { success: true };
  } catch (error) {
    console.error('❌ Error deleting sample discounts:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if discounts are already initialized
 */
export async function checkDiscountsExist() {
  try {
    const vouchersQuery = await getDocs(query(collection(db, 'vouchers'), limit(1)));
    const couponsQuery = await getDocs(query(collection(db, 'coupons'), limit(1)));
    
    return {
      vouchersExist: !vouchersQuery.empty,
      couponsExist: !couponsQuery.empty
    };
  } catch (error) {
    console.error('Error checking discounts:', error);
    return { vouchersExist: false, couponsExist: false };
  }
}
